import { createContext, useContext } from "react";
import type { SubscriptionTier, SubscriptionStatus } from "./plans";
import type { SubscriptionFeatureFlags } from "@/hooks/useSubscription";

// ─── Context value type ───────────────────────────────────────────────────────

/**
 * The subscription state made available to components inside a
 * SubscriptionGate or RequireSubscription tree.
 *
 * Components read this via `useSubscriptionContext()` instead of duplicating
 * Convex query calls, keeping the request to Convex at exactly one per tree.
 */
export type SubscriptionGateContextValue = {
  /** Current plan tier */
  tier: SubscriptionTier;
  /** Current Polar.sh subscription status */
  status: SubscriptionStatus;
  /** Whether the user is currently permitted to access premium features */
  hasAccess: boolean;
  /** True when the user is within the post-expiry grace buffer */
  isInGracePeriod: boolean;
  /**
   * When the client-side 24-hour grace window closes (ms epoch).
   * null when not in a grace period.
   */
  graceEndsAt: number | null;
  /** Granular feature flags derived from the subscription tier */
  featureFlags: SubscriptionFeatureFlags;
  /** True when the user has an admin override that bypasses billing checks */
  isAdmin: boolean;
  /** The Polar.sh subscription ID. null when no active subscription exists. */
  polarSubscriptionId: string | null;
  /**
   * When the current billing period ends (ms epoch).
   * null when not available.
   */
  currentPeriodEnd: number | null;
  /**
   * True when the user is on a free trial (status === "trialing").
   * Components inside a SubscriptionGate can read this to render trial-specific
   * UI (e.g. "view-only" banners, upgrade prompts, trial days remaining).
   */
  isTrialing: boolean;
  /**
   * When the free trial or Polar trial ends (ms epoch). null when not on a trial.
   * Mirrors billingCurrentPeriodEnd when status is "trialing".
   */
  trialEndsAt: number | null;
};

// ─── Context ─────────────────────────────────────────────────────────────────

export const SubscriptionGateContext =
  createContext<SubscriptionGateContextValue | null>(null);

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * useSubscriptionContext
 *
 * Returns the subscription state injected by the nearest SubscriptionGate or
 * RequireSubscription ancestor. Throws if called outside such a tree so that
 * missing providers are caught early.
 *
 * @example
 * ```tsx
 * function CreateServerButton() {
 *   const { featureFlags } = useSubscriptionContext();
 *   if (!featureFlags.canCreateOrg) return null;
 *   return <button>Create Server</button>;
 * }
 * ```
 */
export function useSubscriptionContext(): SubscriptionGateContextValue {
  const ctx = useContext(SubscriptionGateContext);
  if (!ctx) {
    throw new Error(
      "useSubscriptionContext must be called inside a <SubscriptionGate> or <RequireSubscription> tree."
    );
  }
  return ctx;
}
